/**
 * The one way this application talks to the API.
 *
 * Everything here is a consequence of a contract the API side built, tested and
 * published as OpenAPI 3.1 (task 15). Where the plan's sketch and that document
 * disagree, the document wins — it is generated from the renderer's own tables,
 * so it cannot drift from the responses.
 *
 * ## The four things this file exists to get right
 *
 * 1. **One failure envelope, always.** Every `/api/*` failure is
 *    `{"error":{"code","message","request_id","details"}}`. `code` is the
 *    machine contract; `details` is ALWAYS an object; `request_id` is the
 *    support reference and is present even on a URI that matched no route.
 *    `ApiError` is that envelope as a thrown value, so a screen never parses a
 *    response body.
 *
 * 2. **`error.message` is not for the user.** The specification is explicit:
 *    it is "a FIXED string per code... Not for branching, and not localised."
 *    Rendering it puts English in front of an Arabic agent, and for `500` puts
 *    the words "Quote this reference to support" on screen with no reference.
 *    Callers branch on `code` and supply their own translated copy; `message`
 *    is carried for a log, not for a label.
 *
 * 3. **Sanctum cookie auth is a two-step handshake, and the second step is a
 *    header the browser will not add for you.** `GET /sanctum/csrf-cookie`
 *    sets `XSRF-TOKEN`; every unsafe method must echo it as `X-XSRF-TOKEN`, or
 *    the request is a `419 page_expired` before it reaches a controller. Note
 *    that endpoint is NOT under `/api/*`: it has no envelope, no
 *    `X-Request-Id` and no security headers (task 13's closing note).
 *
 * 4. **Credentials, always.** The session is a cookie. `credentials:
 *    'include'` on every request, including the CSRF fetch itself — without it
 *    the browser neither stores the cookie it is being sent nor returns it.
 *
 * ## What is deliberately NOT here
 *
 * - **No bearer-token path.** Machine clients use tokens with abilities and
 *   are not this client's job, but nothing here forecloses one: `authHeaders`
 *   is the single place a token would be added.
 * - **No retry on 429.** The budget is real (5 per account, 20 per address,
 *   15-minute decay) and a client that retries spends its user's remaining
 *   attempts for them. `retryAfterSeconds` is parsed and handed to the caller
 *   to SHOW.
 * - **No logging.** Not of the request, not of the body, not of the error. The
 *   login payload passes through this module and a single `console.log(init)`
 *   would put a password in the browser console, and from there into any
 *   session-replay or error-reporting tool that scrapes it.
 */

import i18n from '../../i18n';

/** The API root. `/api/v1` in development, where Vite proxies to the backend. */
const BASE: string = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * `${BASE}${path}` — for a caller that needs a URL rather than a fetched
 * response, the same way `api.auditLogExportUrl` builds one inline (that
 * function closes over `BASE` directly since it lives in this file; a
 * module outside `client.ts` has no such access, hence this export).
 *
 * `media.ts`'s `mediaUrl` (M-17) is the first outside caller: `GET
 * /case-media/{uuid}` is rendered as a plain `<img src>`, never fetched —
 * this app is Sanctum-cookie-authenticated (`credentials: 'include'`, this
 * file's own docblock, point 4), so the browser attaches the session
 * cookie to an `<img>` request the same as any other same-origin GET. A
 * `fetch`-then-blob-URL indirection would buy nothing here and would cost
 * the private `Cache-Control` the API sends from working the normal way a
 * browser image cache honours it.
 */
export function apiUrl(path: string): string {
  return `${BASE}${path}`;
}

/**
 * Sanctum's CSRF endpoint lives at the APPLICATION root, not under the API
 * prefix, so it cannot be built by appending to `BASE`. When `BASE` is absolute
 * (a deployed SPA on a different host from the API) the endpoint has to follow
 * it to that host; when `BASE` is the root-relative default, so is this.
 */
const CSRF_PATH = '/sanctum/csrf-cookie';

export function csrfUrlFor(base: string): string {
  try {
    // Throws when `base` is relative, which is exactly the case where the
    // root-relative path below is already correct.
    return new URL(CSRF_PATH, base).toString();
  } catch {
    return CSRF_PATH;
  }
}

/** Methods the CSRF pipeline ignores. Anything else needs the header. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export const XSRF_COOKIE = 'XSRF-TOKEN';
export const XSRF_HEADER = 'X-XSRF-TOKEN';

/* ------------------------------------------------------------------ errors */

/**
 * The error envelope, thrown.
 *
 * `requestId` is not decoration: it is the only thing that joins what the agent
 * saw to the row in `application_logs`, and the API mints one even for a
 * request that matched no route. A failure surface that drops it makes every
 * support call start with "can you reproduce it?".
 */
export class ApiError extends Error {
  /** The machine contract. Branch on this, never on `message`. */
  readonly code: string;
  /** Always an object. Field name -> messages, `{}` for every non-validation code. */
  readonly details: Readonly<Record<string, string[]>>;
  /** The support reference. Also on `X-Request-Id`. */
  readonly requestId: string | undefined;
  /** HTTP status, or 0 when the request never got an answer. */
  readonly status: number;
  /** Seconds to wait, from `Retry-After`. Only ever set on a 429. */
  readonly retryAfterSeconds: number | undefined;

  /**
   * The rejected `fetch` behind a `network_error`, for a debugger. Deliberately
   * not rendered anywhere: `TypeError: Failed to fetch` is not a sentence for
   * an agent.
   */
  override cause?: unknown;

  /**
   * The fields are declared and assigned rather than written as constructor
   * parameter properties, which is how the plan's sketch has them. `tsconfig`
   * sets `erasableSyntaxOnly`, so a parameter property is `TS1294` and the
   * plan's `ApiError` does not compile in this repository at all.
   */
  constructor(
    code: string,
    message: string,
    details: Readonly<Record<string, string[]>> = {},
    requestId?: string,
    status = 0,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.requestId = requestId;
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** The client could not reach the API at all — DNS, TLS, offline, CORS. */
export const NETWORK_ERROR_CODE = 'network_error';
/** The session's CSRF token was missing or stale. */
export const PAGE_EXPIRED_CODE = 'page_expired';
/** Not signed in. The gate reads this to decide the login screen is correct. */
export const UNAUTHENTICATED_CODE = 'unauthenticated';
/** A credential failure. The API equalises every cause into this one code. */
export const VALIDATION_FAILED_CODE = 'validation_failed';
/** A budget was spent. Carries `Retry-After`. */
export const TOO_MANY_REQUESTS_CODE = 'too_many_requests';

/* ------------------------------------------------------------------ cookies */

/**
 * Reads a cookie by name.
 *
 * `decodeURIComponent` is load-bearing rather than tidy. Laravel writes the
 * XSRF cookie through Symfony's `Cookie`, which raw-url-encodes the value by
 * default; the token is base64 and its `=` padding therefore arrives as `%3D`.
 * Echoing the raw cookie text back as `X-XSRF-TOKEN` sends a token that does not
 * decrypt, and the symptom is a 419 that looks exactly like having sent no token
 * at all.
 */
export function readCookie(name: string, jar: string = document.cookie): string | null {
  for (const part of jar.split(';')) {
    const separator = part.indexOf('=');
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    try {
      return decodeURIComponent(part.slice(separator + 1).trim());
    } catch {
      /* A malformed percent-escape is not a token; treat it as absent. */
      return null;
    }
  }
  return null;
}

/**
 * In-flight CSRF fetch, so N concurrent requests perform ONE handshake.
 * Without it a screen that fires two mutations at once races two cookie
 * requests, and the second `Set-Cookie` rotates the token the first request is
 * about to send — a 419 that only ever reproduces under load.
 */
let csrfInFlight: Promise<void> | null = null;

async function ensureCsrfCookie(force = false): Promise<void> {
  if (!force && readCookie(XSRF_COOKIE) !== null) return;
  csrfInFlight ??= fetch(csrfUrlFor(BASE), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(() => undefined)
    .finally(() => {
      csrfInFlight = null;
    });
  await csrfInFlight;
}

/** Test seam: the module-level handshake latch must not leak between tests. */
export function resetCsrfState(): void {
  csrfInFlight = null;
}

/* ----------------------------------------------------------------- parsing */

/** `Retry-After` is seconds OR an HTTP-date (RFC 9110). Laravel sends seconds. */
export function parseRetryAfter(header: string | null, now: number = Date.now()): number | undefined {
  if (header === null || header.trim() === '') return undefined;

  if (/^\d+$/.test(header.trim())) return Number.parseInt(header.trim(), 10);

  const at = Date.parse(header);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.round((at - now) / 1000));
}

/**
 * `details` is guaranteed an object by the API. This normalises anyway, because
 * `json_encode([])` is `[]` and the API's own task-11 report records shipping
 * exactly that before it was fixed — and because an intermediary that rewrites
 * an error body is not a hypothetical.
 */
function normaliseDetails(value: unknown): Record<string, string[]> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};

  const out: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(value as Record<string, unknown>)) {
    if (!Array.isArray(messages)) continue;
    out[field] = messages.filter((one): one is string => typeof one === 'string');
  }
  return out;
}

interface ErrorBody {
  error?: {
    code?: unknown;
    message?: unknown;
    request_id?: unknown;
    details?: unknown;
  };
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** `en` or `ar` — never a regional tag the API has no messages for. */
function acceptLanguage(): string {
  return i18n.resolvedLanguage?.startsWith('ar') === true ? 'ar' : 'en';
}

/* ---------------------------------------------------------------- requests */

export interface RequestOptions {
  method?: string;
  /**
   * Serialised as JSON — UNLESS this is a `FormData` instance (M-04's device
   * import, the first caller with an actual file to upload), in which case it
   * is sent as-is. `JSON.stringify(new FormData())` does not throw; it
   * silently produces `'{}'`, because `FormData` has no enumerable own
   * properties for `JSON.stringify` to walk. A caller that built a multipart
   * body would therefore have it swallowed with no error at all — the
   * request would reach the API as an empty JSON object, and
   * `ImportDevicesRequest`'s `'file' => ['required', 'file', ...]` rule
   * would refuse it as a plain validation 422 that never named the real
   * cause. Never appears in the URL, and never in an error.
   */
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * A write that is NOT the user saving something — marking a notification
   * read as the panel opens it, say. Such a request does not engage the save
   * lock (`pendingWrites`, below): locking the whole screen for a side effect
   * the user did not ask to wait for would block the very navigation that
   * triggered it.
   */
  background?: boolean;
}

/* ------------------------------------------------------------- the save lock */

/**
 * How many user-initiated writes are in flight, and who is listening.
 *
 * Every form in the application saves through `request()`, so counting here
 * is what lets the shell lock the screen while ANY save is pending — fields,
 * buttons, dialogs and navigation alike (`SavingGuard`) — without each of
 * the application's forms having to remember to. A form that forgets is
 * exactly the form that gets submitted twice or abandoned half-saved.
 */
let pendingWrites = 0;
const writeListeners = new Set<(pending: number) => void>();

function setPendingWrites(next: number): void {
  pendingWrites = next;
  for (const listener of writeListeners) listener(pendingWrites);
}

export function getPendingWrites(): number {
  return pendingWrites;
}

/** Subscribes to the in-flight write count. Returns the unsubscribe. */
export function subscribeToPendingWrites(listener: (pending: number) => void): () => void {
  writeListeners.add(listener);
  return () => {
    writeListeners.delete(listener);
  };
}

/**
 * A successful response, with the two things `send` throws away.
 *
 * `status` matters because `POST /auth/login` answers **202** — not 200 — when
 * the account holds a second factor, and the body is then a challenge and not a
 * user. A client that reads only `data` cannot tell the two apart and would put
 * the application shell in front of somebody who has presented a password and
 * nothing else.
 *
 * `meta` matters because `POST /auth/mfa/recovery` reports how many recovery
 * codes are left there, and that number is the whole warning a user gets before
 * the account needs an administrator.
 *
 * `envelope` matters because a handful of responses carry an `additional()`
 * key that is neither `data` nor `meta` — `POST /cases`'s `code_claims`
 * (M-06 Task 4) is the first of these: a SIBLING of `data`, not nested under
 * `meta` the way `GET /cases`'s pagination is (`CaseController::store()`
 * calls `->additional(['code_claims' => ...])`, never `['meta' => ...]`).
 * `data`/`meta` alone cannot surface it — this is the whole parsed body,
 * before either is picked out of it, for exactly that case.
 */
export interface ApiResponse<T> {
  status: number;
  data: T;
  meta: unknown;
  envelope: unknown;
}

/**
 * The parsed error envelope of a non-OK response, as the `ApiError` the whole
 * app branches on.
 *
 * Extracted from `request()` rather than copied into `downloadFile()`: a second
 * `new ApiError(...)` is the `rule copied instead of shared` defect, and the
 * two would drift on the first field the envelope grows. Deliberately does NOT
 * cover the CSRF 419 retry — that is a property of the unsafe-method pipeline,
 * not of the mapping, and a plain GET download has no token to refresh.
 *
 * `raw` is the ALREADY-PARSED body, so the caller owns the
 * `.json().catch(() => ({}))` that keeps a truncated or HTML body from
 * throwing a second, uncatchable exception on the failure path.
 */
export function errorFromResponse(response: Response, raw: unknown): ApiError {
  const envelope = (raw as ErrorBody).error ?? {};

  return new ApiError(
    asString(envelope.code) ?? 'unknown',
    asString(envelope.message) ?? 'The request failed.',
    normaliseDetails(envelope.details),
    // `X-Request-Id` is the fallback for a failure whose body is not the
    // envelope at all -- a proxy's HTML 502, say. Task 11 sets both and
    // guarantees they agree.
    asString(envelope.request_id) ?? asString(response.headers.get('X-Request-Id')),
    response.status,
    response.status === 429 ? parseRetryAfter(response.headers.get('Retry-After')) : undefined,
  );
}

/**
 * `request()`, counted. The count wraps the WHOLE logical request — the CSRF
 * cookie fetch and the one 419 retry included — so the lock cannot flicker
 * off between the first attempt and its retry.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
  const method = (options.method ?? 'GET').toUpperCase();
  if (SAFE_METHODS.has(method) || options.background === true) return attempt<T>(path, options);

  setPendingWrites(pendingWrites + 1);
  try {
    return await attempt<T>(path, options);
  } finally {
    setPendingWrites(pendingWrites - 1);
  }
}

async function attempt<T>(
  path: string,
  options: RequestOptions = {},
  retried = false,
): Promise<ApiResponse<T>> {
  const method = (options.method ?? 'GET').toUpperCase();
  const unsafe = !SAFE_METHODS.has(method);

  if (unsafe) await ensureCsrfCookie();

  /*
   * WHAT LANGUAGE THE PERSON IS READING, not what their profile says. The
   * stored `locale` column is only what this client initialises from; the
   * server prefers this header for exactly that reason. Set here rather than
   * at each call site so no request can forget it.
   */
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': acceptLanguage(),
  };

  // A real file upload (M-04's device import), never JSON. The browser sets
  // `Content-Type: multipart/form-data; boundary=...` itself when `fetch` is
  // given a `FormData` body — setting it here would fix the header WITHOUT
  // the boundary the browser generates, and the server would fail to parse
  // the body at all.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  // Set ONLY when there is a JSON body. `Content-Type: application/json` on a
  // GET is a claim about a payload that does not exist, and it costs a CORS
  // preflight: `Accept` is a safelisted request header and
  // `Content-Type: application/json` is not, so adding it turns every
  // cross-origin GET into two round trips.
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

  if (unsafe) {
    const token = readCookie(XSRF_COOKIE);
    if (token !== null) headers[XSRF_HEADER] = token;
  }

  Object.assign(headers, options.headers);

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // The session IS a cookie. Without this the browser sends nothing and
      // stores nothing, and every request is a 401 that looks like a bad login.
      credentials: 'include',
      headers,
      ...(options.body === undefined
        ? {}
        : { body: isFormData ? (options.body as FormData) : JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    // A rejected fetch is offline, DNS, TLS or a CORS refusal. Left to
    // propagate it reaches a screen as a raw `TypeError: Failed to fetch`,
    // which has no code to branch on and no reference to quote. The original is
    // kept as `cause` for a debugger and is never rendered.
    const failure = new ApiError(NETWORK_ERROR_CODE, 'The API could not be reached.', {}, undefined, 0);
    failure.cause = cause;
    throw failure;
  }

  /*
   * ONE path, deliberately. An earlier version returned `{}` early for a 204
   * before calling `.json()`. Mutation testing showed the two mask each other
   * perfectly: `.json()` on an empty body REJECTS, the `catch` already yields
   * `{}`, and no input can tell the branch from its absence. Two paths that
   * cannot be told apart are one path with a spare (task 23 §6.5.5).
   */
  const raw: unknown = await response.json().catch(() => ({}) as unknown);

  if (!response.ok) {
    // One retry, once, and only for a method the CSRF pipeline actually
    // guards. A session that expired mid-shift should cost the agent a
    // re-render, not a re-login — but a 419 that survives a fresh token is a
    // real failure and must surface rather than loop.
    if (response.status === 419 && unsafe && !retried) {
      await ensureCsrfCookie(true);
      return attempt<T>(path, options, true);
    }

    throw errorFromResponse(response, raw);
  }

  return {
    status: response.status,
    data: (raw as { data: T }).data,
    meta: (raw as { meta?: unknown }).meta,
    envelope: raw,
  };
}

/**
 * The common case: a 2xx, and only the payload is wanted.
 *
 * Exported because `src/lib/api/mfa.ts` is a second module of endpoints and the
 * alternative — reaching through `apiInternals`, which exists for tests — would
 * make a production caller borrow a test seam.
 */
export async function send<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await request<T>(path, options)).data;
}

/* -------------------------------------------------------------- the shapes */

/**
 * The `User` resource, exactly as the OpenAPI document declares it: an
 * allowlist, `uuid` and never `id`. The plan's sketch types three of these nine
 * fields.
 */
export interface ApiUser {
  uuid: string;
  username: string;
  full_name: string;
  email: string;
  locale: string;
  theme: string;
  density: string;
  /** POLICY: this account MUST hold a second factor (its own flag, or a role's). */
  mfa_required: boolean;
  /**
   * STATE: a CONFIRMED authenticator exists. Not the same question as
   * `mfa_required` — one is what the account must do, the other what it has
   * done — and an enrolment started but never confirmed reads `false`, because
   * it is not a factor.
   */
  mfa_enabled: boolean;
  roles: readonly string[];
  /**
   * WHAT THE CALLER MAY DO, so the SPA can decide what to render. NOT an
   * authorisation control — every endpoint is still gated by `can:` on the
   * server, and a client that lied about this list would get 403s. It exists
   * so the interface does not offer a control the API will refuse.
   */
  permissions: readonly string[];
  /** Set by an administrator (or a temporary-password reset); routes to a forced change screen. */
  must_change_password: boolean;
}

/**
 * A single active session on the account, as `GET /profile/sessions` lists it.
 *
 * `handle` — not `id` — is the public name: the primary key is never
 * published, and the handle is what `DELETE /profile/sessions/{handle}` takes
 * back. `ip_address` and `user_agent` are nullable because a session can be
 * recorded before either is known. `last_activity` is a Unix timestamp.
 */
export interface SessionSummary {
  handle: string;
  ip_address: string | null;
  user_agent: string | null;
  last_activity: number;
  is_current: boolean;
}

/** The account holds a confirmed second factor and must present a code. */
export const MFA_REQUIRED_STATE = 'mfa_required';
/** A second factor is mandatory for the account and none is enrolled. */
export const ENROLMENT_REQUIRED_STATE = 'enrolment_required';

/**
 * The body of the `202` from `POST /auth/login`.
 *
 * `challengeToken` is a CREDENTIAL with a five-minute life. It is carried in
 * React state and nowhere else — never `localStorage`, never `sessionStorage`,
 * never a cookie — because persisting it would outlive the tab it was issued
 * to and survive the reload that was supposed to end the attempt.
 */
export interface MfaChallenge {
  state: typeof MFA_REQUIRED_STATE | typeof ENROLMENT_REQUIRED_STATE;
  challengeToken: string;
  expiresIn: number;
}

/**
 * What a password buys. EITHER a session or a challenge — never both, and
 * never neither.
 */
export type LoginOutcome =
  | { outcome: 'authenticated'; user: ApiUser }
  | { outcome: 'challenged'; challenge: MfaChallenge };

/** The `202` body as it arrives, before it is renamed to camelCase. */
interface ChallengeBody {
  state?: unknown;
  challenge_token?: unknown;
  expires_in?: unknown;
}

function asChallenge(data: unknown): MfaChallenge {
  const body = (data ?? {}) as ChallengeBody;
  const token = asString(body.challenge_token);
  const state = asString(body.state);

  /*
   * A 202 whose body is not a challenge is not something to shrug at: the
   * screen would post `undefined` as the token, the API would answer its
   * deliberately-identical 422, and the user would be told their code was
   * wrong. Fail here, where the cause is still visible.
   */
  if (
    token === undefined ||
    (state !== MFA_REQUIRED_STATE && state !== ENROLMENT_REQUIRED_STATE)
  ) {
    throw new ApiError('unknown', 'The sign-in response could not be understood.', {}, undefined, 202);
  }

  return {
    state,
    challengeToken: token,
    expiresIn: typeof body.expires_in === 'number' ? body.expires_in : 0,
  };
}

/* -------------------------------------------------------------- audit trail */

/**
 * One row of `audit_log`, exactly as `AuditEntryResource` renders it —
 * verified field-for-field against `app/Http/Resources/AuditEntryResource.php`
 * in the API repository, not guessed from the plan's sketch.
 *
 * `before_state`/`after_state` arrive PARSED, never as a string to parse
 * again: the resource decodes the stored JSON text server-side and reports a
 * tampered or non-object value as `{ _raw: <the original text> }` rather than
 * silently dropping it, so `null` here means "no diff recorded", never "could
 * not be read".
 */
export interface AuditEntry {
  id: number;
  uuid: string;
  occurred_at: string;
  actor_user_id: number | null;
  actor_label: string;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  entity_uuid: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  result: string;
  failure_reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_id: string | null;
  entry_hash: string;
  previous_hash: string | null;
}

/**
 * Every filter `GET /audit-log` accepts. `GET /profile/activity` accepts a
 * strict subset of these — see `api.myActivity` below, which types the one
 * field it must never forward out of its own signature.
 */
export interface AuditQuery {
  actor_user_id?: number;
  /** Free text: the actor's label (`system`), username or full name — a substring. */
  actor?: string;
  /** A prefix: `user.` matches every `user.*` action. */
  action?: string;
  entity_type?: string;
  entity_id?: number;
  request_id?: string;
  from?: string;
  to?: string;
  cursor?: string;
  per_page?: number;
}

/**
 * One page of the trail, as both `GET /audit-log` and `GET /profile/activity`
 * answer it: `{ data, meta }` at ONE level (`AuditEntryResource::collection()
 * ->additional(['meta' => [...]])` on the API side — confirmed against that
 * repository's own `AuditLogIndexTest`, which reads
 * `$response->json('meta.next_cursor')` at the top level, not under `data`).
 *
 * `meta.next_cursor` is an OPAQUE SIGNED STRING. It encodes a signed
 * `(occurred_at, id)` position on the server; nothing here parses, inspects
 * or constructs one — a forged cursor is the server's 422 to raise, not a
 * shape for this client to validate.
 */
export interface AuditPage {
  data: readonly AuditEntry[];
  meta: {
    next_cursor: string | null;
    per_page: number;
  };
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * every audit filter is optional and most calls omit most of them.
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  // `AuditQuery` (and its `Omit<>` variants) carry no index signature, so
  // they are not structurally assignable to `Record<string, ...>` — the
  // parameter is typed as the loose `object` and the entries cast here,
  // rather than forcing every caller to assert its filter type into a shape
  // this function alone cares about.
  for (const [key, value] of Object.entries(params) as [string, string | number | undefined][]) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

/**
 * `attachment; filename="cases-20260827-101500.csv"` -> the filename.
 *
 * Falls back to a generic name rather than throwing: a missing or unparseable
 * header is not a reason to lose a file the server already streamed. The `=`
 * is required by the pattern so an RFC 5987 `filename*=UTF-8''...` parameter
 * falls back cleanly instead of yielding `*=UTF-8''...` as the name.
 *
 * The fallback itself is derived from `accept`, not a single hardcoded
 * string: `'export.csv'` for a proxy that stripped the header on a CSV call
 * is a readable stand-in, but the same fallback on the PDF route (M-15c) would
 * save a file NAMED `.csv` that actually contains `%PDF-1.7` — Windows opens
 * that in Excel as gibberish, and the bug report says "the PDF export is
 * broken" when the download itself worked fine. `accept` is already exactly
 * the caller's declared content type, so it is the one signal here that
 * needs no new parameter to carry.
 */
function filenameFrom(disposition: string | null, accept: string): string {
  const match = disposition?.match(/filename\s*=\s*"?([^";]+)"?/);
  const fallback = accept.startsWith('application/pdf') ? 'case.pdf' : 'export.csv';

  return match?.[1]?.trim() || fallback;
}

/**
 * A file download, not a JSON call.
 *
 * THE SAME URL RETURNS EITHER: a `200` streams the file (CSV on the six
 * export routes, PDF on the case-PDF route added by M-15c — seven GETs
 * through here in total), a `422` returns the JSON cap refusal. `response.ok`
 * is checked BEFORE the body is touched, so a refusal goes back through the
 * same `ApiError` machinery every other call uses rather than being saved as
 * a file called `cases-....csv` containing `{"message":"..."}`.
 *
 * No CSRF token and no 419 retry, deliberately: every route through here is a
 * GET (the six exports and the case PDF alike), `SAFE_METHODS` excludes GETs
 * from the CSRF pipeline entirely, and `credentials: 'include'` is the whole
 * of what carries the session.
 */
export async function downloadFile(
  path: string,
  params: Record<string, string | number | boolean | undefined> = {},
  accept = 'text/csv, application/json',
): Promise<{ blob: Blob; filename: string }> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}${queryString(params)}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: accept,
        /*
         * The refusal is SERVER-LOCALISED. `CsvExport::guard()` builds the
         * over-the-cap 422 with `__('errors.export_too_large', [...])`
         * (`CsvExport.php:110`), which resolves against the request's
         * negotiated locale — so without this header an Arabic-reading
         * supervisor gets the fallback locale's message. Same
         * `acceptLanguage()` `request()` uses; the CSV path needs it for
         * exactly the same reason.
         */
        'Accept-Language': acceptLanguage(),
      },
    });
  } catch (cause) {
    // Same shape as `request()`'s network failure: a rejected fetch left to
    // propagate reaches a screen as a raw `TypeError: Failed to fetch`, with
    // no code to branch on. The original is kept as `cause`, never rendered.
    const failure = new ApiError(NETWORK_ERROR_CODE, 'The API could not be reached.', {}, undefined, 0);
    failure.cause = cause;
    throw failure;
  }

  if (!response.ok) {
    // The refusal path. `.catch` keeps a truncated, empty or HTML error body
    // from throwing a raw parse exception in place of the readable error.
    const raw: unknown = await response.json().catch(() => ({}) as unknown);
    throw errorFromResponse(response, raw);
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition'), accept),
  };
}

export const api = {
  /**
   * Exchange credentials for a session cookie — OR for a second-factor
   * challenge.
   *
   * Every credential failure — unknown account, wrong password, disabled
   * account — is the same 422 with the same message in the same time. The
   * specification says it in capitals: "Do not build a client that tries to
   * tell them apart; it cannot, deliberately."
   *
   * ONLY a 200 is a session. The test is written that way round on purpose: an
   * `if (status === 202)` with an authenticated fallback would treat any future
   * 2xx as a completed sign-in, and the cost of guessing wrong in that
   * direction is the shell rendered to somebody who has presented one factor.
   */
  /**
   * `remember` DEFAULTS TO `false` AND IS ALWAYS SENT, NOT OMITTED WHEN
   * UNCHECKED (Task 4, M-18). Extends the SESSION this issues to 14 days
   * instead of the ordinary ~2 hours — it is not a remember-token and does
   * not touch the second factor: an account with one confirmed is challenged
   * exactly the same either way, and the `202` branch below carries no
   * session for it to extend. On THAT branch the flag has to be resubmitted
   * to `mfaApi.verify()` once the challenge is answered — see that
   * function — because this request never issues the credential.
   */
  login: async (
    username: string,
    password: string,
    remember = false,
    signal?: AbortSignal,
  ): Promise<LoginOutcome> => {
    const { status, data } = await request<unknown>('/auth/login', {
      method: 'POST',
      body: { username, password, remember },
      signal,
    });

    if (status === 200) return { outcome: 'authenticated', user: data as ApiUser };

    return { outcome: 'challenged', challenge: asChallenge(data) };
  },

  me: (signal?: AbortSignal) => send<ApiUser>('/auth/me', { signal }),

  logout: () => send<{ status: string }>('/auth/logout', { method: 'POST' }),

  /** The four fields a person may change about themselves. */
  updateProfile: (fields: Partial<Pick<ApiUser, 'full_name' | 'locale' | 'theme' | 'density'>>) =>
    send<ApiUser>('/profile', { method: 'PATCH', body: fields }),

  changePassword: (currentPassword: string, password: string) =>
    send<ApiUser>('/profile/password', {
      method: 'PUT',
      body: {
        current_password: currentPassword,
        password,
        password_confirmation: password,
      },
    }),

  sessions: () => send<SessionSummary[]>('/profile/sessions'),

  revokeSession: (handle: string) =>
    send<{ revoked: number }>(`/profile/sessions/${encodeURIComponent(handle)}`, {
      method: 'DELETE',
    }),

  revokeOtherSessions: () => send<{ revoked: number }>('/profile/sessions', { method: 'DELETE' }),

  /** Always resolves. The API answers 202 whether or not the address exists. */
  requestPasswordReset: (email: string) =>
    send<{ status: 'accepted' }>('/auth/password/forgot', { method: 'POST', body: { email } }),

  /**
   * `passwordConfirmation` is a SEPARATE argument, sent as given — unlike
   * `changePassword` below, which still synthesises it from `password`
   * (a pre-existing gap in a merged milestone, out of this endpoint's
   * scope). Synthesising it here would make Laravel's `confirmed` rule on
   * `ResetPasswordRequest` compare `password_confirmation` to itself: always
   * true, which means the wire never carries a real confirmation check and
   * the only place a mismatched confirmation is ever caught is client-side.
   * Callers that already enforce equality before calling this (Task 7's
   * `ResetPasswordScreen`) still pass two arguments that happen to be equal
   * — the point is that this function no longer forces that outcome by
   * construction, so the server's own `confirmed` rule is a real, second
   * control rather than a comparison against itself.
   */
  resetPassword: (email: string, code: string, password: string, passwordConfirmation: string) =>
    send<{ status: 'reset' }>('/auth/password/reset', {
      method: 'POST',
      body: { email, code, password, password_confirmation: passwordConfirmation },
    }),

  /* ------------------------------------------------------------- audit trail */

  /**
   * The full trail, administrators only (`audit.view`). The filters travel as
   * a query string on a GET — never a body, which would be a claim about a
   * payload this request does not have and would cost a CORS preflight for
   * nothing (see `request()`'s own note on `Content-Type`).
   *
   * DELIBERATELY NOT `send<AuditPage>()`, even though every other method here
   * uses `send`. `send()` resolves to `request()`'s `.data` ALONE and throws
   * `meta` away — correct for `sessions()`, `me()` and the rest, where the
   * caller never needs it, but `AuditPage.meta.next_cursor` is the ONLY way a
   * caller pages through the trail. Typing this call `send<AuditPage>()`
   * would compile — the cast inside `request()` is unchecked — but resolve
   * every call to a bare `AuditEntry[]` with no `.data`/`.meta` on it: the
   * exact "downstream reads `undefined`" failure this file's methods exist to
   * avoid, just reached from the opposite direction. Composing the page
   * explicitly from `request()`'s `data` and `meta` keeps the same promise
   * `send()` makes everywhere else — this still resolves to `AuditPage`
   * directly, never to `request()`'s own `{status, data, meta}` envelope.
   */
  auditLog: async (params: AuditQuery = {}): Promise<AuditPage> => {
    const { data, meta } = await request<readonly AuditEntry[]>(`/audit-log${queryString(params)}`);
    return { data, meta: meta as AuditPage['meta'] };
  },

  /**
   * The caller's own rows, any authenticated user, scoped server-side to
   * `actor_user_id = <the caller>`. `actor_user_id` is typed OUT of the
   * parameter (`Omit<>`) AND stripped again at runtime below — the `Omit<>`
   * alone only stops a caller who goes through the type checker; TypeScript
   * erases types at build time, so a caller that reaches past it with a cast
   * (or plain JS, which sees no types at all) would still hand this function
   * an object carrying the field, and `queryString()` serialises whatever
   * keys the object actually has. `ActivityController` never reads
   * `actor_user_id` at all — passing one is silently inert there — and a
   * client that still SENT it would invite a future reader to believe it
   * narrows something.
   */
  myActivity: async (params: Omit<AuditQuery, 'actor_user_id'> = {}): Promise<AuditPage> => {
    const { actor_user_id: _actorUserId, ...rest } = params as AuditQuery;
    const { data, meta } = await request<readonly AuditEntry[]>(`/profile/activity${queryString(rest)}`);
    return { data, meta: meta as AuditPage['meta'] };
  },

  /**
   * A URL, not a request. `GET /audit-log/export` streams `text/csv` for a
   * browser to download — navigating to this URL (or an `<a href>` pointed
   * at it) makes that download happen; FETCHING it here would pull the whole
   * CSV into memory as a string with no way to hand it back to the person as
   * a file. Administrators only (`audit.view`) — the browser's own
   * cookie-based session authenticates the navigation, the same as any other
   * same-origin download link.
   */
  auditLogExportUrl: (params: AuditQuery = {}): string => `${BASE}/audit-log/export${queryString(params)}`,
};

/** Exposed for tests and for a future caller that needs a raw path. */
export const apiInternals = { BASE, send, request };
